import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import landingPageAPI from '../../services/landingPage';
import { HomeBody, HomeHeader } from './HomePageView';
import './LandingPage.css';

// Public home page (Sprint 7 "Home Template"). SuperAdmin edits it in place at
// /home-page-editor; the job fairs, employers and organizations are live data.
// SuperAdmin can also switch the page off there, and then `/` goes straight to the login page.

const VIEW_CTX = { edit: false, errorFor: () => undefined, onChange: () => {} };
// The first fetch shows nothing for this long, so a switched-off page goes to login without a
// flash of the dark page; a slow connection still gets a visible loading message after it.
const LOADING_MESSAGE_DELAY_MS = 800;

export default function LandingPage() {
  const { user } = useAuth();
  const [data, setData] = useState(() => landingPageAPI.peekPublic());
  const [status, setStatus] = useState(() => (landingPageAPI.peekPublic() ? 'ready' : 'loading'));
  const [showLoading, setShowLoading] = useState(false);
  const errorRef = useRef(null);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      setData(await landingPageAPI.getPublic());
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (!landingPageAPI.peekPublic()) load();
  }, [load]);

  useEffect(() => {
    if (status !== 'loading' || data) return undefined;
    const timer = setTimeout(() => setShowLoading(true), LOADING_MESSAGE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [status, data]);

  useEffect(() => {
    if (status === 'error') errorRef.current?.focus();
  }, [status]);

  const content = data?.content;
  const ctx = { ...VIEW_CTX, content };

  if (status === 'ready' && data?.enabled === false) {
    return <Navigate to="/login" replace />;
  }

  // Until the first answer arrives it isn't known whether the page is on, so draw nothing of it
  if (status === 'loading' && !data) {
    return (
      <main id="main-content" className="hp-pending" tabIndex={-1} aria-busy="true">
        <p role="status" className={showLoading ? 'hp-pending-text' : 'sr-only'}>Loading…</p>
      </main>
    );
  }

  return (
    <div className="home-page home-page--public">
      <Helmet>
        <title>{content?.pageTitle || 'ABILITY Job Fair'}</title>
        {content?.metaDescription && <meta name="description" content={content.metaDescription} />}
      </Helmet>
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <HomeHeader ctx={ctx} nextEvent={data?.nextEvent} user={user} />

      <main id="main-content" className="hp-main" tabIndex={-1} aria-busy={status === 'loading'}>
        {status === 'error' && (
          <div className="hp-error" role="alert" tabIndex={-1} ref={errorRef}>
            <h1 className="hp-error-title">We couldn't load the home page</h1>
            <p>Please try again. You can still <Link to="/login">log in</Link> or <Link to="/register">register as a job seeker</Link>.</p>
            <button type="button" className="hp-btn hp-btn--primary" onClick={load}>Try again</button>
          </div>
        )}

        {status === 'ready' && content && <HomeBody ctx={ctx} data={data} level={1} />}
      </main>
    </div>
  );
}
